import React, { Suspense } from 'react'
import getContentTypeConfig from './config'
import dynamic from 'next/dynamic'
import { shape, string, bool, object, func, elementType } from 'prop-types'

import 'react-perfect-scrollbar/dist/css/styles.css'
const PerfectScrollbar = dynamic(() => import('react-perfect-scrollbar'))
const Faq = dynamic(() => import('@components/seo'))

/**
 * Render a content type
 *
 * @param Component
 * @param data
 * @returns {*}
 */
const renderContentType = (
    Component,
    data,
    AddToWishlist,
    AddToWishlistLabel,
    globalOptions,
    isLazyLoad,
    ProductTile,
    productQuery,
    extendedContentTypeConfig,
    isMenu
) => {
    return (
        <Component
            {...data}
            AddToWishlist={AddToWishlist}
            AddToWishlistLabel={AddToWishlistLabel}
            globalOptions={globalOptions}
            isLazyLoad={isLazyLoad}
            ProductTile={ProductTile}
            productQuery={productQuery}
            isMenu={isMenu}
        >
            {data.children.map((childTreeItem, i) => (
                <ContentTypeFactory
                    key={i}
                    data={childTreeItem}
                    AddToWishlist={AddToWishlist}
                    AddToWishlistLabel={AddToWishlistLabel}
                    globalOptions={globalOptions}
                    isLazyLoad={isLazyLoad}
                    ProductTile={ProductTile}
                    productQuery={productQuery}
                    extendedContentTypeConfig={extendedContentTypeConfig}
                    isMenu={isMenu}
                />
            ))}
        </Component>
    )
}

/**
 * Create an instance of a content type component based on configuration
 *
 * @param data
 * @returns {*}
 * @constructor
 */
const ContentTypeFactory = (props) => {
    const {
        data,
        AddToWishlist,
        AddToWishlistLabel,
        isLazyLoad,
        ProductTile,
        productQuery,
        extendedContentTypeConfig,
        isMenu
    } = props
    const { isHidden, ...restData } = data

    const globalOptions = {}

    if (isHidden) {
        return null
    }

    const contentTypeConfig = getContentTypeConfig(
        restData.contentType,
        extendedContentTypeConfig
    )
    if (contentTypeConfig && contentTypeConfig.component) {
        const renderComponent = (
            <Suspense fallback="">
                {renderContentType(
                    contentTypeConfig.component,
                    restData,
                    AddToWishlist,
                    AddToWishlistLabel,
                    globalOptions,
                    isLazyLoad,
                    ProductTile,
                    productQuery,
                    extendedContentTypeConfig,
                    isMenu
                )}
            </Suspense>
        )
        if (restData.cssClasses?.includes('seo-faq-schema')) {
            const faqContent =
                restData.contentType === 'html'
                    ? restData.html
                    : restData.content

            return (
                <>
                    <Faq content={faqContent} />
                    {renderComponent}
                </>
            )
        } else if (restData.cssClasses?.includes('corra-scrollbar')) {
            return (
                <PerfectScrollbar>
                    <div className="scrollbar-wrap">{renderComponent}</div>
                </PerfectScrollbar>
            )
        } else {
            return renderComponent
        }
    }

    return null
}

/**
 * Props for {@link ContentTypeFactory}
 *
 * @typedef props
 *
 * @property {Object} data An object containing the class names for the RichContent
 * @property {Function} AddToWishlist Return wishlist element and options
 * @property {String} AddToWishlistLabel Wishlist label
 * @property {Boolean} isLazyLoad Load content when it is visible
 * @property {String} productQuery Content
 * @property {Object} extendedContentTypeConfig An object containing the class names for the RichContent
 * @property {Boolean} isMenu
 */
ContentTypeFactory.propTypes = {
    data: shape({
        isHidden: bool,
        contentType: string,
        content: string,
        html: string
    }),
    AddToWishlist: func,
    AddToWishlistLabel: string,
    isLazyLoad: bool,
    productQuery: string,
    ProductTile: elementType,
    extendedContentTypeConfig: object,
    isMenu: bool
}

export default ContentTypeFactory
